import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  checkSession,
  passkeyLoginStart,
  passkeyLoginFinish,
  requestSignInCode,
  verifySignInCode,
  AuthError,
  type User,
} from '../lib/authClient';
import styles from './Login.module.css';

interface LoginProps {
  onAuthSuccess: (user: User) => void;
  initialError?: string;
}

type LoginStep = 'idle' | 'submitting' | 'code-entry' | 'error';

function mapError(err: unknown): string {
  // User cancelled or timed out the biometric prompt
  if (err instanceof Error && err.name === 'NotAllowedError') {
    return 'Sign in was cancelled. Tap the button to try again.';
  }
  if (err instanceof AuthError) {
    if (err.status === 401) {
      return 'Sign in failed. Try again or use a sign-in code.';
    }
    if (err.status === 400) {
      return err.message.includes('email') ? 'Please enter your email address.' : err.message;
    }
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}

export default function Login({ onAuthSuccess, initialError }: LoginProps) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<LoginStep>(initialError ? 'error' : 'idle');
  const [errorMessage, setErrorMessage] = useState(initialError ?? '');
  // Whether a code was requested: keeps the code-entry screen up while it submits or shows an error
  const [codeRequested, setCodeRequested] = useState(false);

  const isSubmitting = step === 'submitting';

  function requireEmail(): boolean {
    if (!email.trim()) {
      setStep('error');
      setErrorMessage('Please enter your email address.');
      return false;
    }
    return true;
  }

  async function handlePasskeyLogin() {
    if (!requireEmail()) return;
    setStep('submitting');
    try {
      const options = await passkeyLoginStart(email.trim());
      const response = await startAuthentication({ optionsJSON: options });
      await passkeyLoginFinish(response);
      const user = await checkSession();
      if (user) {
        onAuthSuccess(user);
      } else {
        setStep('error');
        setErrorMessage('Sign in succeeded but session could not be confirmed. Please reload.');
      }
    } catch (err) {
      setStep('error');
      setErrorMessage(mapError(err));
    }
  }

  async function handleRequestCode() {
    if (!requireEmail()) return;
    setStep('submitting');
    try {
      await requestSignInCode(email.trim());
      // Shown for any email: the server never says whether it has an account
      setCodeRequested(true);
      setCode('');
      setErrorMessage('');
      setStep('code-entry');
    } catch (err) {
      setStep('error');
      setErrorMessage(mapError(err));
    }
  }

  async function handleVerifyCode() {
    if (!code.trim()) {
      setStep('error');
      setErrorMessage('Please enter the 6-digit code.');
      return;
    }
    setStep('submitting');
    try {
      const user = await verifySignInCode(email.trim(), code.trim());
      onAuthSuccess(user);
    } catch {
      setStep('error');
      setErrorMessage("That code didn't work. Check it, or send a new code.");
    }
  }

  if (codeRequested) {
    return (
      <main className={styles.container}>
        <div className={styles.codeSent}>
          <p className={styles.codeSentText}>
            If that email has an account, a 6-digit sign-in code is on its way. It expires in
            10 minutes.
          </p>

          <label className={styles.label}>
            Sign-in code
            <input
              className={styles.emailInput}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleVerifyCode();
              }}
              disabled={isSubmitting}
              aria-label="Sign-in code"
            />
          </label>

          <button
            className={styles.primaryButton}
            onClick={handleVerifyCode}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Please wait…' : 'Sign in with code'}
          </button>

          <button
            className={styles.secondaryButton}
            onClick={handleRequestCode}
            disabled={isSubmitting}
          >
            Send a new code
          </button>

          <button
            className={styles.secondaryButton}
            onClick={() => {
              setCodeRequested(false);
              setCode('');
              setStep('idle');
              setErrorMessage('');
            }}
            disabled={isSubmitting}
          >
            Use a different email
          </button>

          {step === 'error' && errorMessage && (
            <p className={`${styles.statusArea} ${styles.errorMessage}`} role="alert">
              {errorMessage}
            </p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>HONAWICO</h1>
        <p className={styles.subtitle}>Sign in to continue</p>
      </header>

      <div className={styles.form}>
        <label className={styles.label}>
          Email address
          <input
            className={styles.emailInput}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePasskeyLogin();
            }}
            disabled={isSubmitting}
            aria-label="Email address"
          />
        </label>

        <button
          className={styles.primaryButton}
          onClick={handlePasskeyLogin}
          disabled={isSubmitting}
          aria-label="Sign in with this phone"
        >
          {isSubmitting ? 'Please wait…' : 'Sign in with this phone'}
        </button>

        <div className={styles.divider}>or</div>

        <button
          className={styles.secondaryButton}
          onClick={handleRequestCode}
          disabled={isSubmitting}
          aria-label="Email me a sign-in code"
        >
          Email me a sign-in code
        </button>

        {step === 'error' && errorMessage && (
          <p className={`${styles.statusArea} ${styles.errorMessage}`} role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </main>
  );
}
